// Create PO on phones and small tablets (2026-10-21; Ahmed P1-a, P2-b, P3-a, P4-b; mockup canvas QgufCDP5cwvvQ7AbSStaop, row 2).
// The approved lines are three piles to work through instead of one long list:
//   no supplier yet  – a supplier is suggested from where the item was last bought or a supplier's price list,
//                      and is set only when procurement taps (history is an aid, never automatic)
//   need a price     – suppliers with at least one line still at 0
//   ready to order   – suppliers whose lines all have a price
// A supplier opens as one PO; its lines start unticked. Laptops keep the full table (POCreate).
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Empty, Input, InputNumber, Modal, Select, message } from 'antd';
import { ArrowLeftOutlined, RightOutlined, SearchOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getLookups, refineMRLine } from '../../api';
import { historyHint, usualPrice } from '../../utils/priceSource';
import StickyActionBar from '../../components/responsive/StickyActionBar';
import type { ApprovableLine } from './POCreate';

type Pile = 'none' | 'price' | 'ready';

interface Suggestion {
  vendorId: number;
  vendorName: string;
  /** 'bought' = where the item was last bought; 'list' = on that supplier's price list */
  why: 'bought' | 'list';
  date: string | null;
  price: number | null;
}

interface SupplierPile {
  vendorId: number;
  vendorName: string;
  lines: ApprovableLine[];
  unpriced: number;
  value: number;
  waiting: number;
}

interface SuggestedGroup {
  vendorId: number;
  vendorName: string;
  items: Array<{ line: ApprovableLine; s: Suggestion }>;
}

export interface POCreatePhoneProps {
  lines: ApprovableLine[];
  /** True when a line passes the search box (the same rule as the laptop list). */
  matches: (l: ApprovableLine) => boolean;
  query: string;
  setQuery: (q: string) => void;
  selectedIds: Set<number>;
  setSelectedIds: (s: Set<number>) => void;
  enteredPrices: Record<number, { price: number | null; usual: boolean }>;
  setGiven: (id: number, price: number | null, usual: boolean) => void;
  reviewed: boolean;
  setReviewed: (v: boolean) => void;
  notes: string;
  setNotes: (v: string) => void;
  creating: boolean;
  onCreate: () => void;
  /** Procurement and admins choose suppliers and refine lines. */
  canRefine: boolean;
  onRefine: (l: ApprovableLine) => void;
  /** Label of the cancel action for this user, or null when they cannot cancel. */
  cancelLabel: string | null;
  onCancelSelected: () => void;
  /** Reloads the lines without leaving this screen. */
  reload: () => Promise<void>;
  renderItem: (l: ApprovableLine) => React.ReactNode;
}

const DAY = 864e5;
const waitingDays = (l: ApprovableLine) => (l.approvedOn ? Math.max(0, Math.floor((Date.now() - new Date(l.approvedOn).getTime()) / DAY)) : 0);
const hasPrice = (l: ApprovableLine) => Number(l.unitPrice) > 0;
const savedValue = (l: ApprovableLine) => Number(l.lineTotal) || Number(l.qty) * Number(l.unitPrice);
const money = (n: number, decimals = 0) => n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const shortDate = (s?: string | null) => (s ? dayjs(s).format('D MMM') : '');
const count = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const waitingText = (d: number) => `waiting ${count(d, 'day')}`;

const MUTED = '#5f6672';
const box: React.CSSProperties = { background: '#fff', borderRadius: 10, overflow: 'hidden' };
const meta: React.CSSProperties = { fontSize: 12, color: MUTED };

/** Where the suggested supplier comes from, in the officer's words. */
const reasonText = (s: Suggestion) =>
  s.why === 'bought'
    ? `last bought there${s.date ? ` · ${shortDate(s.date)}` : ''}${s.price ? ` · ${money(s.price, 2)} each` : ''}`
    : `on ${s.vendorName}'s price list${s.price ? ` · ${money(s.price, 2)}` : ''}`;

const POCreatePhone: React.FC<POCreatePhoneProps> = (props) => {
  const { lines, matches, query, setQuery, selectedIds, setSelectedIds, enteredPrices, setGiven, reviewed, setReviewed, canRefine } = props;
  const [vendors, setVendors] = useState<Array<{ id: number; name: string }>>([]);
  const [pile, setPile] = useState<Pile | null>(null);
  const [openVendor, setOpenVendor] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [picker, setPicker] = useState<{ lines: ApprovableLine[]; title: string } | null>(null);
  const [pickVendor, setPickVendor] = useState<number | undefined>();
  const [showAllUnsuggested, setShowAllUnsuggested] = useState(false);

  // Ahmed 2026-10-21: the supplier's back arrow returns to exactly where the list was when the supplier was tapped.
  const listScrollY = useRef<number | null>(null);
  // Layout effect: the list is back in the page but not yet painted, so there is no jump from the top.
  useLayoutEffect(() => {
    if (openVendor != null || listScrollY.current == null) return;
    const y = listScrollY.current;
    listScrollY.current = null;
    window.scrollTo(0, y);
  }, [openVendor]);

  useEffect(() => {
    getLookups().then((l: any) => setVendors((l.vendors || []).map((v: any) => ({ id: v.id, name: v.name })))).catch(() => {});
  }, []);
  const vendorIdByName = useMemo(() => new Map(vendors.map((v) => [v.name.trim().toLowerCase(), v.id])), [vendors]);

  // Suggestions use only purchase history and supplier price lists (P2-b): never the item code's ending.
  const suggestionFor = (l: ApprovableLine): Suggestion | null => {
    if (l.prefillVendorId && (l.prefillSource === 'po' || l.prefillSource === 'list')) {
      return { vendorId: l.prefillVendorId, vendorName: l.prefillVendorName || 'this supplier', why: l.prefillSource === 'po' ? 'bought' : 'list', date: l.prefillDate ?? null, price: l.prefillPrice ?? null };
    }
    const latest = l.priceHistory?.latest;
    const id = latest?.vendorName ? vendorIdByName.get(latest.vendorName.trim().toLowerCase()) : undefined;
    if (latest && id) return { vendorId: id, vendorName: latest.vendorName!, why: 'bought', date: latest.date, price: latest.price };
    return null;
  };

  const visible = lines.filter(matches);
  const noneLines = visible.filter((l) => !l.vendor?.id);

  const suppliers: SupplierPile[] = useMemo(() => {
    const map = new Map<number, SupplierPile>();
    for (const l of visible) {
      if (!l.vendor?.id) continue;
      const p = map.get(l.vendor.id) || { vendorId: l.vendor.id, vendorName: l.vendor.name, lines: [], unpriced: 0, value: 0, waiting: 0 };
      p.lines.push(l);
      if (hasPrice(l)) p.value += savedValue(l); else p.unpriced += 1;
      p.waiting = Math.max(p.waiting, waitingDays(l));
      map.set(l.vendor.id, p);
    }
    return Array.from(map.values()).sort((a, b) => b.waiting - a.waiting || a.vendorName.localeCompare(b.vendorName));
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps
  const ready = suppliers.filter((s) => s.unpriced === 0);
  const needPrice = suppliers.filter((s) => s.unpriced > 0);

  const { groups, unsuggested } = useMemo(() => {
    const map = new Map<number, SuggestedGroup>();
    const none: ApprovableLine[] = [];
    for (const l of noneLines) {
      const s = suggestionFor(l);
      if (!s) { none.push(l); continue; }
      const g = map.get(s.vendorId) || { vendorId: s.vendorId, vendorName: s.vendorName, items: [] };
      g.items.push({ line: l, s });
      map.set(s.vendorId, g);
    }
    return {
      groups: Array.from(map.values()).sort((a, b) => b.items.length - a.items.length || a.vendorName.localeCompare(b.vendorName)),
      unsuggested: none,
    };
  }, [noneLines, vendorIdByName]); // eslint-disable-line react-hooks/exhaustive-deps
  const suggestedCount = groups.reduce((n, g) => n + g.items.length, 0);
  const suggestedFor = (vendorId: number) => groups.find((g) => g.vendorId === vendorId)?.items.length || 0;
  const waitingWith = (vendorId: number) => suppliers.find((s) => s.vendorId === vendorId)?.lines.length || 0;

  // Default pile: ready first, then pricing, then choosing suppliers.
  const activePile: Pile = pile || (ready.length ? 'ready' : needPrice.length ? 'price' : 'none');

  const toggleExpanded = (key: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const assign = async (targets: ApprovableLine[], vendorId: number, vendorName: string, note: (l: ApprovableLine) => string, key: string) => {
    setBusy(key);
    let done = 0;
    const failed: string[] = [];
    for (const l of targets) {
      try {
        await refineMRLine(l.id, { vendorId, refinementNotes: note(l) });
        done += 1;
      } catch (err: any) {
        failed.push(`${l.mrHeader?.mrId} line ${l.lineNo}: ${err?.response?.data?.error || 'failed'}`);
      }
    }
    if (done) message.success(`${count(done, 'line')} now with ${vendorName}`);
    if (failed.length) message.error(failed.join(' · '), 8);
    await props.reload();
    setBusy(null);
  };
  const suggestionNote = (s: Suggestion) =>
    `Supplier chosen on Create PO (suggested: ${s.why === 'bought' ? `last bought from ${s.vendorName}${s.date ? ` on ${shortDate(s.date)}` : ''}` : `on ${s.vendorName}'s price list`})`;
  const applySuggestion = (g: SuggestedGroup, items = g.items, key = `g${g.vendorId}`) =>
    assign(items.map((i) => i.line), g.vendorId, g.vendorName, (l) => suggestionNote(items.find((i) => i.line.id === l.id)!.s), key);

  const openPicker = (targets: ApprovableLine[], title: string) => { setPickVendor(undefined); setPicker({ lines: targets, title }); };
  const confirmPicker = async () => {
    if (!picker || !pickVendor) return;
    const v = vendors.find((x) => x.id === pickVendor);
    const targets = picker.lines;
    setPicker(null);
    await assign(targets, pickVendor, v?.name || 'the supplier', () => 'Supplier chosen on Create PO', 'picker');
  };

  const openSupplier = (vendorId: number) => {
    listScrollY.current = window.scrollY;
    setSelectedIds(new Set()); // P4-b: the officer ticks what goes on the PO
    setReviewed(false);
    setOpenVendor(vendorId);
    window.scrollTo(0, 0);
  };
  const closeSupplier = () => { setSelectedIds(new Set()); setReviewed(false); setOpenVendor(null); };

  // ---------- one line waiting for a supplier ----------
  const lineToPlace = (l: ApprovableLine, s: Suggestion | null) => (
    <article key={l.id} style={{ padding: '10px 12px', borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={meta}>{l.mrHeader?.mrId} · line {l.lineNo}{l.costCenter?.code ? ` · ${l.costCenter.code}` : ''}</div>
      {props.renderItem(l)}
      <div style={{ fontSize: 13 }}>{Number(l.qty)} {l.unit?.name || ''}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={meta}>{s ? reasonText(s) : 'never bought, on no price list'}</span>
        {canRefine && (
          <Button size="middle" onClick={() => openPicker([l], `${l.mrHeader?.mrId} · line ${l.lineNo}`)} disabled={!!busy}>
            {s ? 'Other supplier' : 'Choose supplier'}
          </Button>
        )}
      </div>
    </article>
  );

  // ---------- the supplier sheet: one supplier, one PO ----------
  if (openVendor != null) {
    const sheetLines = lines.filter((l) => l.vendor?.id === openVendor);
    const vendorName = sheetLines[0]?.vendor?.name || vendors.find((v) => v.id === openVendor)?.name || 'Supplier';
    const picked = sheetLines.filter((l) => selectedIds.has(l.id));
    const given = (l: ApprovableLine) => (Number(enteredPrices[l.id]?.price) > 0 ? Number(enteredPrices[l.id]?.price) : 0);
    const value = (l: ApprovableLine) => (hasPrice(l) ? savedValue(l) : Number(l.qty) * given(l));
    const unpricedPicked = picked.filter((l) => !hasPrice(l) && !given(l));
    const total = picked.reduce((n, l) => n + value(l), 0);
    const mrCount = new Set(sheetLines.map((l) => l.mrHeader?.mrId)).size;
    const waiting = sheetLines.reduce((m, l) => Math.max(m, waitingDays(l)), 0);
    const tick = (id: number, on: boolean) => {
      const next = new Set(selectedIds);
      if (on) next.add(id); else next.delete(id);
      setSelectedIds(next);
    };
    const allPicked = sheetLines.length > 0 && picked.length === sheetLines.length;
    const extra = groups.find((g) => g.vendorId === openVendor);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={closeSupplier} aria-label="Back to the piles" />
          <div style={{ minWidth: 0 }}>
            <div style={meta}>{sheetLines.some((l) => !hasPrice(l)) ? 'Need a price' : 'Ready to order'}</div>
            <div style={{ fontWeight: 600, fontSize: 16 }} dir="auto">{vendorName}</div>
          </div>
        </div>

        {sheetLines.length === 0 ? (
          <div style={{ ...box, padding: 16 }}><Empty description="Nothing left for this supplier" /></div>
        ) : (
          <>
            <section style={{ ...box, padding: 12, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, fontSize: 13 }}>
              <div><div style={meta}>Lines</div><b>{sheetLines.length}</b></div>
              <div><div style={meta}>From</div><b>{count(mrCount, 'MR')}</b></div>
              <div><div style={meta}>Waiting</div><b>{count(waiting, 'day')}</b></div>
            </section>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: MUTED }}>Tick the lines to order.</span>
              <Button type="link" style={{ padding: 0 }} onClick={() => setSelectedIds(allPicked ? new Set() : new Set(sheetLines.map((l) => l.id)))}>
                {allPicked ? 'Untick all' : `Tick all ${sheetLines.length}`}
              </Button>
            </div>

            <section aria-label="Lines" style={box}>
              {sheetLines.map((l, i) => {
                const usual = usualPrice(l);
                const hint = historyHint(l);
                return (
                  <article key={l.id} style={{ padding: 12, display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr)', gap: 8, borderTop: i ? '1px solid #f0f0f0' : undefined }}>
                    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 2, minHeight: 36 }}>
                      <Checkbox checked={selectedIds.has(l.id)} onChange={(e) => tick(l.id, e.target.checked)} aria-label={`Order ${l.mrHeader?.mrId} line ${l.lineNo}`} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                      <div style={meta}>{l.mrHeader?.mrId} · line {l.lineNo}{l.costCenter?.code ? ` · ${l.costCenter.code}` : ''}</div>
                      {props.renderItem(l)}
                      {hasPrice(l) ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                          <span>{Number(l.qty)} {l.unit?.name || ''} × {money(Number(l.unitPrice), 2)}</span>
                          <b>{money(savedValue(l), 2)}</b>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13 }}><b>{Number(l.qty)} {l.unit?.name || ''}</b> ×</span>
                            <InputNumber
                              min={0}
                              step={1}
                              precision={2}
                              inputMode="decimal"
                              placeholder="0.00"
                              status={given(l) ? undefined : 'error'}
                              style={{ width: 110 }}
                              value={enteredPrices[l.id]?.price ?? undefined}
                              onChange={(v) => { setGiven(l.id, v == null ? null : Number(v), false); if (v) tick(l.id, true); }}
                              aria-label={`Unit price for ${l.mrHeader?.mrId} line ${l.lineNo}`}
                            />
                            {usual != null && (
                              <Button onClick={() => { setGiven(l.id, usual, true); tick(l.id, true); }}>Use {money(usual, 2)}</Button>
                            )}
                            {given(l) > 0 && <b style={{ marginLeft: 'auto' }}>= {money(value(l), 2)}</b>}
                          </div>
                          <span style={{ fontSize: 12, color: hint ? MUTED : '#cf1322' }}>{hint || 'no purchase history, type a price'}</span>
                        </>
                      )}
                      {canRefine && (
                        <Button type="link" size="small" style={{ padding: 0, alignSelf: 'flex-start' }} onClick={() => props.onRefine(l)}>Refine</Button>
                      )}
                    </div>
                  </article>
                );
              })}
            </section>

            {extra && canRefine && (
              <section style={{ ...box, border: '1px solid #91caff' }}>
                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <b>{count(extra.items.length, 'more line')} suggested for {vendorName}</b>
                  <span style={meta}>They have no supplier yet. Add them and they appear above, unticked.</span>
                  <Button type="primary" loading={busy === `x${extra.vendorId}`} disabled={!!busy && busy !== `x${extra.vendorId}`} onClick={() => applySuggestion(extra, extra.items, `x${extra.vendorId}`)} style={{ alignSelf: 'flex-start', minHeight: 44 }}>
                    Add {extra.items.length === 1 ? 'it' : `all ${extra.items.length}`} to {vendorName}
                  </Button>
                </div>
                {extra.items.map(({ line, s }) => lineToPlace(line, s))}
              </section>
            )}

            {picked.length > 0 && (
              <>
                <Input.TextArea rows={2} value={props.notes} onChange={(e) => props.setNotes(e.target.value)} placeholder="PO notes (optional)" />
                <div style={{ ...box, padding: 12, fontSize: 13 }}>
                  <Checkbox checked={reviewed} onChange={(e) => setReviewed(e.target.checked)}>
                    I have reviewed each line total and confirm the quantities, units and prices are correct
                  </Checkbox>
                </div>
                {props.cancelLabel && (
                  <Button danger type="link" style={{ alignSelf: 'flex-start', padding: 0 }} onClick={props.onCancelSelected}>
                    {props.cancelLabel} ({picked.length})
                  </Button>
                )}
              </>
            )}

            <StickyActionBar>
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <div style={{ fontSize: 12, color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {picked.length === 0
                    ? 'Tick the lines to order'
                    : `${count(picked.length, 'line')}${unpricedPicked.length ? ` · ${unpricedPicked.length} need${unpricedPicked.length === 1 ? 's' : ''} a price` : !reviewed ? ' · tick the review box' : ''}`}
                </div>
                <div style={{ fontSize: 17, fontWeight: 600 }}>{money(total, 2)} EGP</div>
              </div>
              <Button
                type="primary"
                icon={<ShoppingCartOutlined />}
                loading={props.creating}
                onClick={props.onCreate}
                disabled={picked.length === 0 || !reviewed || unpricedPicked.length > 0}
              >
                Create PO
              </Button>
            </StickyActionBar>
          </>
        )}
        {pickerModal()}
      </div>
    );
  }

  // ---------- the piles ----------
  function pickerModal() {
    return (
      <Modal
        open={!!picker}
        title={picker ? `Choose a supplier · ${picker.title}` : ''}
        okText="Use this supplier"
        okButtonProps={{ disabled: !pickVendor }}
        onOk={confirmPicker}
        onCancel={() => setPicker(null)}
        destroyOnClose
      >
        <Select
          showSearch
          autoFocus
          placeholder="Search suppliers"
          aria-label="Supplier"
          optionFilterProp="label"
          style={{ width: '100%' }}
          value={pickVendor}
          onChange={setPickVendor}
          options={vendors.map((v) => ({ value: v.id, label: v.name }))}
        />
      </Modal>
    );
  }

  const tile = (key: Pile, n: number, label: string, sub: string, tone: { border: string; bg: string; ink: string }) => {
    const on = activePile === key;
    return (
      <button
        type="button"
        onClick={() => setPile(key)}
        aria-pressed={on}
        style={{
          minHeight: 88, textAlign: 'left', border: on ? `2px solid ${tone.border}` : '1px solid #d9d9d9', background: on ? tone.bg : '#fff',
          borderRadius: 10, padding: on ? 9 : 10, display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer', font: 'inherit', color: 'inherit',
        }}
      >
        <span style={{ fontSize: 24, fontWeight: 700, color: tone.ink, lineHeight: 1.2 }}>{n}</span>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12, color: MUTED }}>{sub}</span>
      </button>
    );
  };

  const supplierRow = (s: SupplierPile, mode: 'ready' | 'price') => {
    const extra = suggestedFor(s.vendorId);
    const mrs = new Set(s.lines.map((l) => l.mrHeader?.mrId)).size;
    return (
      <button
        key={s.vendorId}
        type="button"
        onClick={() => openSupplier(s.vendorId)}
        style={{ width: '100%', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto 16px', gap: 8, alignItems: 'center', minHeight: 60, padding: '8px 12px', border: 0, borderTop: '1px solid #f0f0f0', background: '#fff', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
      >
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <b dir="auto">{s.vendorName}</b>
          <span style={meta}>
            {mode === 'price' ? `${s.unpriced} of ${count(s.lines.length, 'line')} need a price` : `${count(s.lines.length, 'line')}${mrs > 1 ? ` from ${mrs} MRs` : ''}`} · {waitingText(s.waiting)}
          </span>
          {extra > 0 && <span style={{ fontSize: 12, color: '#0958d9' }}>+{count(extra, 'line')} suggested for this supplier</span>}
        </span>
        <span style={{ fontWeight: 600 }}>{s.value > 0 ? money(s.value) : ''}</span>
        <RightOutlined style={{ color: '#8c8c8c', fontSize: 12 }} aria-hidden />
      </button>
    );
  };

  const groupBlock = (g: SuggestedGroup) => {
    const key = `g${g.vendorId}`;
    const open = expanded.has(key);
    const bought = g.items.filter((i) => i.s.why === 'bought').length;
    const listed = g.items.length - bought;
    const mrs = new Set(g.items.map((i) => i.line.mrHeader?.mrId)).size;
    const already = waitingWith(g.vendorId);
    return (
      <section key={key} aria-label={g.vendorName} style={box}>
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <b style={{ fontSize: 15 }} dir="auto">{g.vendorName}</b>
            <span style={{ fontSize: 13, color: MUTED, whiteSpace: 'nowrap' }}>{count(g.items.length, 'line')} · {count(mrs, 'MR')}</span>
          </div>
          <span style={meta}>{[listed ? `${listed} on the price list` : '', bought ? `${bought} last bought there` : ''].filter(Boolean).join(' · ')}</span>
          {already > 0 && <span style={{ fontSize: 12, color: '#0958d9' }}>{count(already, 'line')} already wait for {g.vendorName}: these join the same PO</span>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {canRefine && (
              <Button type="primary" style={{ minHeight: 44 }} loading={busy === key} disabled={!!busy && busy !== key} onClick={() => applySuggestion(g)}>
                Use {g.vendorName} for {g.items.length === 1 ? 'it' : `all ${g.items.length}`}
              </Button>
            )}
            <Button style={{ minHeight: 44 }} aria-expanded={open} onClick={() => toggleExpanded(key)}>{open ? 'Hide lines' : 'See lines'}</Button>
          </div>
        </div>
        {open && g.items.map(({ line, s }) => lineToPlace(line, s))}
      </section>
    );
  };

  const shownUnsuggested = showAllUnsuggested ? unsuggested : unsuggested.slice(0, 5);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Input
        allowClear
        size="large"
        prefix={<SearchOutlined style={{ color: '#8a94a6' }} />}
        placeholder="Search supplier, MR, item, cost center"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <nav aria-label="Piles" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
        {tile('none', noneLines.length, 'No supplier yet', `lines · ${suggestedCount} suggested`, { border: '#fa8c16', bg: '#fff7e6', ink: '#ad4e00' })}
        {tile('price', needPrice.length, 'Need a price', `suppliers · ${count(needPrice.reduce((n, s) => n + s.lines.length, 0), 'line')}`, { border: '#1677ff', bg: '#e6f4ff', ink: '#0958d9' })}
        {tile('ready', ready.length, 'Ready to order', `suppliers · ${count(ready.reduce((n, s) => n + s.lines.length, 0), 'line')}`, { border: '#52c41a', bg: '#f6ffed', ink: '#237804' })}
      </nav>

      {activePile === 'ready' && (
        <>
          <p style={{ margin: 0, fontSize: 13, color: MUTED, lineHeight: 1.45 }}>
            A supplier is ready when every one of its lines has a price. Open it, tick the lines and create the PO.
          </p>
          <section aria-label="Ready to order" style={box}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 12px 8px' }}>
              <b style={{ fontSize: 15 }}>Ready to order</b>
              <span style={meta}>longest waiting first</span>
            </div>
            {ready.length ? ready.map((s) => supplierRow(s, 'ready')) : <div style={{ padding: 16 }}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No supplier is fully priced yet" /></div>}
          </section>
        </>
      )}

      {activePile === 'price' && (
        <>
          <p style={{ margin: 0, fontSize: 13, color: MUTED, lineHeight: 1.45 }}>
            These suppliers have lines without a price. Open one to type prices or use the usual price, then order.
          </p>
          <section aria-label="Need a price" style={box}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 12px 8px' }}>
              <b style={{ fontSize: 15 }}>Need a price</b>
              <span style={meta}>longest waiting first</span>
            </div>
            {needPrice.length ? needPrice.map((s) => supplierRow(s, 'price')) : <div style={{ padding: 16 }}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Every supplier's lines have a price" /></div>}
          </section>
        </>
      )}

      {activePile === 'none' && (
        <>
          {noneLines.length === 0 ? (
            <div style={{ ...box, padding: 16 }}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Every line has a supplier" /></div>
          ) : (
            <section style={{ background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, lineHeight: 1.45 }}>
              <b style={{ fontSize: 14 }}>{suggestedCount} of {count(noneLines.length, 'line')} have a suggested supplier</b>
              <span>
                Taken from where the item was last bought or a supplier's price list. Nothing changes until you tap
                {canRefine ? '. You can still pick another supplier for any line.' : '. Procurement chooses the suppliers.'}
              </span>
            </section>
          )}

          {groups.map(groupBlock)}

          {unsuggested.length > 0 && (
            <section aria-label="No suggestion" style={{ ...box, border: '1px solid #ffd591' }}>
              <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <b style={{ fontSize: 15 }}>No suggestion</b>
                  <span style={{ fontSize: 13, color: MUTED }}>{count(unsuggested.length, 'line')}</span>
                </div>
                <span style={meta}>Never bought and on no supplier's price list. Choose a supplier for each.</span>
              </div>
              {shownUnsuggested.map((l) => lineToPlace(l, null))}
              {unsuggested.length > shownUnsuggested.length && (
                <Button type="link" style={{ margin: '4px 12px 10px' }} onClick={() => setShowAllUnsuggested(true)}>
                  Show all {unsuggested.length}
                </Button>
              )}
            </section>
          )}
        </>
      )}
      {pickerModal()}
    </div>
  );
};

export default POCreatePhone;
